import React, {useEffect, useRef, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {useNavigate} from "react-router-dom";
import ErrorModal from "../../../reusableComponents/ErrorModal";
import SuccessModal from "../../../reusableComponents/SuccessModal";
import {removeLastPayment} from "../../../store/reducers/accountSlice";
import {getPaymentStatus} from "../../../utils/apis";
import "./index.scss";
import {getPaymentStatusPublic} from "../../../utils/apis/payment";
import ProcessingModal from "../../../reusableComponents/processing modal";

import Confetti from "react-confetti";
import successSound from "../../../assets/SoundFiles/tada-fanfare-a-6313.mp3";
import {useWindowSize} from "react-use";

// The processing screen is driven by the payment status API instead of a fixed wait:
// we re-check every POLL_INTERVAL_MS and close it the moment the webhook has landed,
// so it stays on screen only for as long as the confirmation actually takes.
const POLL_INTERVAL_MS = 2000;
// Bank-confirmed methods - the "waiting for your bank to confirm" screens - routinely
// take more than a minute to settle. The old 45s budget ran out while the payment was
// still on its way, which is how a charge that went through seconds later was
// announced as "not confirmed yet". Nothing is reported until the provider has
// answered, so the budget has to cover how long that answer realistically takes.
const MAX_POLL_MS = 150000;
// However the lookup ends up stalling, the loader is never the last thing on
// screen for longer than this. It has to outlast the polling budget - cutting in
// earlier is exactly what made the screen announce an outcome we never received.
const VERIFY_TIMEOUT_MS = MAX_POLL_MS + 20000;

// Every payment-result route renders this component. The provider redirect only
// says where the user came back from - the webhook is what actually settles a
// payment - so the status API is queried on these routes too, and a charge that
// did go through is honoured even when the redirect claims otherwise.
const ABANDONED_STATUSES = ["cancelled", "declined", "failure"];

// The status API answers 200 for any payment it can find - including a failed one
// ({..., "payment_status": "failed"}) - so the HTTP code only says the lookup
// worked. payment_status is what actually says how the payment went, and these are
// the values the backend uses (same vocabulary as the payment history filter).
const SUCCEEDED_PAYMENT_STATUSES = ["succeeded", "success", "successful", "completed", "captured", "paid"];
const PENDING_PAYMENT_STATUSES = ["open", "pending", "processing", "created", "initiated", "in_progress"];

// Everything the API can report that is neither settled nor still moving.
const FAILURE_MESSAGES = {
  failed: "You have not been charged. Please try again or use a different payment method.",
  declined: "Your card issuer declined the payment. Please try again with a different card.",
  rejected: "The payment was rejected, so no account was created. Please try again or contact support.",
  expired: "The payment session expired before it completed. Please start the checkout again.",
  cancelled: "Nothing has been charged. You can start the checkout again whenever you are ready.",
};
const PENDING_MESSAGE = "Payment is in progress, if successful you will receive your account in 15-30 mins";
const UNKNOWN_MESSAGE = "Please check your payment history in a few minutes - if the amount was charged, your account will follow shortly.";

// What a guest is handed on their way to sign-in. The heading and the styling of the
// popup follow the outcome as well, so a payment we have not been told succeeded is
// never announced under a green "Successful!" banner.
const SIGNIN_SUCCESS_STATE = {
  status: "success",
  title: "Payment confirmed",
  message: "Your payment has been confirmed. Please sign in to check its status and view your account details.",
};
const SIGNIN_PENDING_STATE = {
  status: "info",
  title: "Payment in progress",
  message: "Your payment provider has not confirmed the payment yet. Please sign in to check its status and view your account details.",
};
const SIGNIN_UNCONFIRMED_STATE = {
  status: "info",
  title: "Payment not confirmed yet",
  message: "We have not received a final status from your payment provider yet. Please sign in to check its status and view your account details.",
};

// Every outcome names itself in the popup heading instead of a blanket "Error".
const STATUS_TITLES = {
  succeeded: "Payment confirmed",
  pending: "Payment in progress",
  cancelled: "Payment cancelled",
  failed: "Payment failed",
  declined: "Payment declined",
  rejected: "Payment rejected",
  expired: "Payment expired",
  unknown: "Payment not confirmed yet",
};

const readPaymentStatus = (responseAPI) => {
  const body = responseAPI?.data || responseAPI?.response?.data;
  return String(body?.payment_status || body?.data?.payment_status || "").toLowerCase();
};

// "succeeded" | "pending" | the reported failure ("failed", "declined", ...) |
// "unknown" when the lookup itself failed or came back without a status.
const classifyPayment = (responseAPI) => {
  const value = readPaymentStatus(responseAPI);
  if (value) {
    if (SUCCEEDED_PAYMENT_STATUSES.includes(value)) return "succeeded";
    if (PENDING_PAYMENT_STATUSES.includes(value)) return "pending";
    return value;
  }
  // Body carried no payment_status - fall back to the HTTP code, which is what this
  // screen used to key off on its own.
  return responseAPI?.status === 200 ? "succeeded" : "unknown";
};

// The message for anything that is not a settled payment.
const describePayment = (outcome) => {
  if (outcome === "pending") return PENDING_MESSAGE;
  if (!outcome || outcome === "unknown") return UNKNOWN_MESSAGE;
  return FAILURE_MESSAGES[outcome] || `Your payment could not be completed (${outcome}). Please try again or contact support if the amount was charged.`;
};

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const trackPurchase = (amount) => {
  // fbq comes from the Meta pixel script - a missing pixel must never turn a
  // successful payment into a failure.
  if (typeof window.fbq !== "function") return;
  try {
    window.fbq("track", "Purchase", {
      currency: "USD",
      value: amount,
    });
  } catch (e) {
    console.warn("Purchase tracking failed:", e);
  }
};

// The payment this screen reports on is identified by what checkout stored, and
// that store is cleared as a normal part of finishing up - removeLastPayment(),
// the 401 handler's deAuthenticate()/purge, a reload. Read fresh on every
// render, it could therefore vanish mid-flow and take the whole screen with it,
// which is what left this route showing an empty page. Read once instead.
const readStoredJSON = (key) => {
  try {
    return JSON.parse(localStorage.getItem(key) || "null");
  } catch (e) {
    return null;
  }
};

function PostPayment({status}) {
  const reduxChallengeId = useSelector((state) => state.account.selectedChallenge);
  const paymentHistory = useSelector((state) => state.account.paymentHistory);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isModalOpen, setModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  // What the API reported in payment_status - drives which message the user sees.
  const [statusDetail, setStatusDetail] = useState(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const {idToken} = useSelector((state) => state.auth);

  // What checkout left behind, captured once on mount: everything below is
  // cleared while this screen is still open, and re-reading it mid-flow is what
  // let the screen lose the payment it was reporting on. The public status lookup
  // also needs the payer's email, which the initiate-payment response does not
  // always carry - the address typed at checkout is the fallback.
  const [checkout] = useState(() => ({
    payment: paymentHistory?.lastPayment || readStoredJSON("lastPayment"),
    email: readStoredJSON("checkout_details")?.formData?.email || "",
    challengeId: localStorage.getItem("checkout_challenge_id"),
  }));

  const lastPayment = checkout.payment;
  const paymentEmail = lastPayment?.data?.email || checkout.email;
  const challengeId = reduxChallengeId || checkout.challengeId || lastPayment?.data?.challenge_id || lastPayment?.data?.challenge;

  const isAbandoned = ABANDONED_STATUSES.includes(status);

  // Where an abandoned/failed payment sends the user back to - the dashboard
  // checkout for a signed-in user, the public checkout page for a guest.
  const checkoutPath = challengeId ? (idToken ? `/funding-evaluation/payments/${challengeId}` : `/funding-evaluation/public/checkout/${challengeId}`) : idToken ? "/" : "/signin";

  const {width, height} = useWindowSize();
  const [showConfetti, setShowConfetti] = useState(false);

  const isUnmounted = useRef(false);
  const hasStartedRef = useRef(false);

  useEffect(() => {
    isUnmounted.current = false;
    return () => {
      isUnmounted.current = true;
    };
  }, []);

  // A cancelled redirect means the provider is telling us the user backed out
  // before paying, so there is no result to look up and nothing to tell them:
  // they go straight back to checkout with the details they filled in still on
  // the form.
  //
  // This deliberately does NOT consult the payment status API the way a declined
  // or failed redirect does. That lookup is an authenticated request, and a 401
  // or 403 on it trips the global axios interceptor, which logs the user out,
  // purges persisted state and reloads the page - which lands them back on this
  // route with no token, where the component renders nothing at all. Staking the
  // way out of a cancellation on a network call that can do that is what left the
  // user on a blank screen. Nothing was charged, so there is nothing here worth
  // that risk: a charge that did somehow settle is still picked up by the webhook
  // and shows in payment history.
  const isCancelled = status === "cancelled";

  useEffect(() => {
    if (!isCancelled) return;

    try {
      // The checkout page repopulates from this and clears it on mount.
      localStorage.setItem("checkout_restore", "true");
      localStorage.removeItem("payment_in_progress");
      localStorage.removeItem("lastPayment");
    } catch (e) {
      console.error("Error clearing payment state:", e);
    }
    dispatch(removeLastPayment());

    navigate(checkoutPath, {replace: true});
  }, [isCancelled, checkoutPath, navigate, dispatch]);

  const playSuccessSound = () => {
    try {
      const audio = new Audio(successSound);
      audio.volume = 0.6;
      audio.play().catch((e) => {
        console.warn("Sound autoplay blocked by browser policy:", e);
      });
    } catch (e) {
      console.warn("Sound autoplay blocked:", e);
    }
  };

  const handleModal = () => {
    setModalOpen(false);
  };

  useEffect(() => {
    setModalOpen(true);
  }, [status]);

  const [response, setResponse] = useState(null);

  // There is no page behind this route - only the loader and the result popup -
  // so one of the two always has to be on screen. Anything before an outcome is
  // known counts as still verifying, including the gap between mount and the
  // lookup raising its own loading flag.
  const isVerifying = isLoading || !paymentStatus;

  // Live counter for the processing screen, so it shows real progress instead of
  // a static "expected time" line.
  useEffect(() => {
    if (!isVerifying) {
      setElapsedSeconds(0);
      return;
    }
    const startedAt = Date.now();
    const timer = setInterval(() => {
      setElapsedSeconds(Math.floor((Date.now() - startedAt) / 1000));
    }, 1000);
    return () => clearInterval(timer);
  }, [isVerifying]);

  // Last line of defence for the screen the user is left looking at: whatever
  // goes wrong in the lookup - a request that never settles, a redirect that
  // carries nothing we can identify the payment by - the loader gives way to a
  // popup that at least tells them where to check.
  useEffect(() => {
    if (!isVerifying || isCancelled) return;
    const timer = setTimeout(() => {
      if (isUnmounted.current) return;
      setIsLoading(false);
      setPaymentStatus((current) => current || "unverified");
    }, VERIFY_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [isVerifying, isCancelled]);

  useEffect(() => {
    // Guarded across every branch below: removeLastPayment always writes a fresh
    // paymentHistory object, so dispatching it unguarded re-triggers this effect
    // (paymentHistory is a dependency) and loops until React bails with
    // "Maximum update depth exceeded".
    if (hasStartedRef.current) return;
    // Cancellations are handled by the effect above - no lookup, no popup.
    if (isCancelled) return;

    const paymentId = lastPayment?.data?.id;
    const isRapyd = lastPayment?.data?.payment_mode === "rapyd";
    // The private status endpoint needs the token, the public one needs the email.
    const canQueryStatus = Boolean(paymentId) && Boolean(idToken || paymentEmail);

    const clearStoredPayment = () => {
      if (paymentHistory?.lastPayment) {
        dispatch(removeLastPayment());
      }
      localStorage.removeItem("lastPayment");
    };

    const clearCheckoutDetails = () => {
      localStorage.removeItem("checkout_details");
      localStorage.removeItem("checkout_challenge_id");
      localStorage.removeItem("checkout_coupon");
      localStorage.removeItem("checkout_restore");
      localStorage.removeItem("payment_in_progress");
    };

    // payment_in_progress is what PrivateRoutes uses to pull a user who wandered
    // off mid-payment back to checkout. Once the provider has redirected them
    // here the payment is no longer in flight, so the flag has to be released -
    // left set, every attempt to reach the dashboard bounces back to checkout.
    // The checkout form still has to repopulate after a payment that did not go
    // through, so that intent moves to its own key, which the checkout page
    // consumes on mount.
    const releasePaymentInProgress = () => {
      try {
        localStorage.setItem("checkout_restore", "true");
        localStorage.removeItem("payment_in_progress");
      } catch (e) {
        console.error("Error releasing payment_in_progress:", e);
      }
    };

    // A payment the API reports as cancelled was never charged, so there is
    // nothing to tell the user - back to checkout, replacing the history entry so
    // the back button does not return to this result route. (A cancelled redirect
    // never gets this far; it is handled without a lookup above.)
    const returnToCheckout = () => {
      clearStoredPayment();
      navigate(checkoutPath, {replace: true});
    };

    // 🎉 CONFETTI + SOUND
    const celebrate = (amount) => {
      setShowConfetti(true);
      playSuccessSound();
      trackPurchase(amount);
    };

    // A lookup that told us nothing falls back to what the redirect itself claimed,
    // so the popup can still name the outcome. "failure" stays unknown on purpose -
    // the provider calling it failed is not proof the charge did not settle.
    const resolveOutcome = (apiOutcome) => {
      if (apiOutcome !== "unknown") return apiOutcome;
      if (status === "cancelled") return "cancelled";
      if (status === "declined") return "declined";
      return "unknown";
    };

    // The private status endpoint needs the token, the public one the payer's email -
    // the question being asked is the same either way.
    const checkPaymentStatusOnce = async () => {
      if (idToken) {
        return getPaymentStatus(idToken, {payment_id: paymentId});
      }
      return getPaymentStatusPublic({payment_id: paymentId, email: paymentEmail});
    };

    // Re-checks the payment status until the API reports something final, or until we
    // give up. Only a still-moving payment ("open"/"processing") - or a lookup that
    // told us nothing at all, which is what a payment the webhook has not reached yet
    // looks like - is worth waiting on; a reported failure is final, so we stop there
    // rather than sit on the loader.
    //
    // A guest gets exactly the same treatment as a signed-in user. Asking the public
    // endpoint once and then announcing whatever that single answer happened to be is
    // what told people their payment was unconfirmed moments before it settled.
    const pollForPaymentStatus = async () => {
      const startedAt = Date.now();
      let responseAPI = null;
      let outcome = "unknown";

      while (!isUnmounted.current) {
        responseAPI = await checkPaymentStatusOnce();
        outcome = classifyPayment(responseAPI);

        if (outcome !== "pending" && outcome !== "unknown") break;
        if (Date.now() - startedAt >= MAX_POLL_MS - POLL_INTERVAL_MS) break;

        await wait(POLL_INTERVAL_MS);
      }

      return {outcome, responseAPI};
    };

    // The user came back from a cancelled, declined or failed redirect. Ask the API
    // what actually happened before deciding what to show: a charge that settled is
    // treated as a success, and every other answer gets a popup naming it.
    const handleAbandonedPayment = async () => {
      setIsLoading(true);

      // The redirect landing on cancelled/declined/failure is not the processor's
      // verdict - a bank that has not confirmed yet sends the user back here while the
      // webhook is still on its way. Asking once and announcing that single answer is
      // what reported "not confirmed yet" seconds before the payment settled, so this
      // waits for a real status exactly like the success route does. A genuine failure
      // is reported immediately and breaks the loop on the first pass, so nothing that
      // really did fail sits on the loader.
      const {outcome: apiOutcome, responseAPI} = await pollForPaymentStatus();
      if (isUnmounted.current) return;

      const outcome = resolveOutcome(apiOutcome);
      setResponse(responseAPI?.data || responseAPI?.response?.data);
      setStatusDetail(outcome);
      setIsLoading(false);

      if (outcome === "succeeded") {
        setPaymentStatus("success");
        clearCheckoutDetails();
        celebrate(lastPayment?.data?.amount);

        await wait(2500);
        clearStoredPayment();

        if (idToken) {
          navigate("/funding-evaluation-payment/payment-history/");
        } else {
          navigate("/signin", {state: SIGNIN_SUCCESS_STATE});
        }
        return;
      }

      // Nothing was charged and the user already knows why - straight back to
      // checkout, with the details they filled in still on the form.
      if (outcome === "cancelled") {
        returnToCheckout();
        return;
      }

      clearStoredPayment();

      if (outcome === "pending") {
        // Still moving despite the redirect - say so instead of sending the user
        // back to checkout, where they could pay a second time.
        setPaymentStatus("processing");
        return;
      }

      // Cancelled, failed, declined, rejected, expired - the popup names which one,
      // and closing it is what returns the user to checkout.
      setPaymentStatus("failure");
    };

    const getPaymentStatusForChallenge = async () => {
      // Nothing on record to identify the payment by, so the redirect is all we
      // know the outcome by, and the popup says that much rather than leaving the
      // screen empty. The stored payment is only discarded on an abandoned
      // redirect: there is no PersistGate, so the token can still be rehydrating
      // on first paint, and the re-mount that follows needs it to run the real
      // lookup.
      if (!canQueryStatus && !isRapyd) {
        hasStartedRef.current = true;
        releasePaymentInProgress();
        if (isAbandoned) clearStoredPayment();
        setStatusDetail(resolveOutcome("unknown"));
        // Not a failure on the success route - "unknown" reads as information
        // rather than an error, and does not offer to charge the card again.
        setPaymentStatus(isAbandoned ? "failure" : "unverified");
        return;
      }

      if (!idToken && !paymentEmail) {
        navigate("/signin", {replace: true});
        return;
      }

      hasStartedRef.current = true;
      releasePaymentInProgress();

      if (isRapyd) {
        if (status === "success") {
          setPaymentStatus("success");
          clearCheckoutDetails();
          celebrate(lastPayment?.data?.amount);

          await wait(2000);
          clearStoredPayment();
          navigate("/funding-evaluation-payment/payment-history/");
          return;
        }
        // A rapyd payment we can still identify gets the same status check as any
        // other. Without an id there is nothing to ask about, so the redirect is
        // all we know the outcome by - and a user who cancelled out of the rapyd
        // page must not be told a payment is in progress.
        if (isAbandoned) {
          if (canQueryStatus) {
            await handleAbandonedPayment();
            return;
          }
          clearStoredPayment();
          setStatusDetail(resolveOutcome("unknown"));
          setPaymentStatus("failure");
          return;
        }
        setStatusDetail("pending");
        setPaymentStatus("processing");
        return;
      }

      if (isAbandoned) {
        await handleAbandonedPayment();
        return;
      }

      try {
        setIsLoading(true);

        // The loader stays up until the provider has actually answered - signed in or
        // not, the user is never told anything the status API has not reported yet.
        const {outcome, responseAPI} = await pollForPaymentStatus();
        if (isUnmounted.current) return;

        setResponse(responseAPI?.data || responseAPI?.response?.data);
        setStatusDetail(outcome);
        setIsLoading(false);

        if (outcome === "succeeded") {
          // Confirmed: the confetti and the success popup mean the payment settled,
          // never that it is merely on its way.
          setPaymentStatus("success");
          clearCheckoutDetails();
          celebrate(lastPayment?.data?.amount);

          await wait(2500);

          if (idToken) {
            navigate("/funding-evaluation-payment/payment-history/");
          } else {
            navigate("/signin", {state: SIGNIN_SUCCESS_STATE});
          }
          return;
        }

        if (outcome === "pending" || outcome === "unknown") {
          // Nothing final even after the full budget - tell the user it is still in
          // progress rather than holding them on a loader indefinitely.
          setPaymentStatus("processing");
          return;
        }

        // The API reported a failure ("failed", "declined", ...) - the popup names
        // which one, and closing it returns the user to checkout so they can retry.
        setPaymentStatus("failure");
      } catch (error) {
        console.error("Error fetching payment status:", error);
        setIsLoading(false);
        setPaymentStatus("failure");
        navigate(checkoutPath);
      } finally {
        dispatch(removeLastPayment());
        localStorage.removeItem("lastPayment");
        setIsLoading(false);
      }
    };

    getPaymentStatusForChallenge();
    // lastPayment is captured once on mount, so listing it cannot re-trigger this.
  }, [idToken, navigate, dispatch, paymentHistory, lastPayment, paymentEmail, challengeId, checkoutPath, status, isAbandoned, isCancelled]);

  useEffect(() => {
    if (paymentStatus === "success") {
      const timer = setTimeout(() => setShowConfetti(false), 5000);
      return () => clearTimeout(timer);
    }
  }, [paymentStatus]);

  const accountList = useSelector((state) => state?.account?.accountList?.data);
  let msg = accountList?.length > 0 ? "Congratulations! Payment was successful" : "You'll shortly receive an email with your account login credentials";

  const resultOutcome = (() => {
    if (paymentStatus === "processing" || statusDetail === "pending") return "pending";
    if (statusDetail && statusDetail !== "unknown") return statusDetail;
    // No usable payment_status: fall back to what the old copy keyed off.
    if (response?.detail === "Unable to fetch the payment status" || lastPayment?.data?.payment_mode === "rapyd") return "pending";
    return "unknown";
  })();

  // The button and where it leads follow whatever the popup says the outcome was,
  // so a heading reading "Payment in progress" can never sit above a "Try again"
  // that drops the user back on checkout. A payment the API still calls
  // open/processing is not an error, and neither is one the user cancelled
  // themselves - both read as "Information", not "Error".
  const isPendingResult = resultOutcome === "pending";
  const isInfoResult = isPendingResult || resultOutcome === "cancelled" || resultOutcome === "unknown";
  const resultMessage = describePayment(resultOutcome);
  const resultTitle = STATUS_TITLES[resultOutcome];

  // Closing the success popup before the automatic redirect has fired must still
  // lead somewhere, or it closes onto an empty page.
  const successDestination = () => {
    if (idToken) {
      navigate("/funding-evaluation-payment/payment-history/");
    } else {
      navigate("/signin", {state: SIGNIN_SUCCESS_STATE});
    }
  };

  // Only a payment we know did not go through sends the user back to checkout to
  // retry. One that is still moving, or one we could not confirm either way, goes
  // to payment history instead - offering "Try again" there is how a user ends up
  // paying twice for the same account.
  const shouldReturnToCheckout = !isPendingResult && resultOutcome !== "unknown";

  // This route renders nothing but the popup, so closing it always has to lead
  // somewhere - otherwise the user is left looking at an empty page.
  const closeDestination = () => {
    if (shouldReturnToCheckout) {
      navigate(checkoutPath);
      return;
    }
    if (idToken) {
      navigate("/funding-evaluation-payment/payment-history/");
    } else {
      // A payment we could not confirm must not be handed on under a green
      // "Successful!" banner - the sign-in popup repeats exactly what we know.
      navigate("/signin", {state: isPendingResult ? SIGNIN_PENDING_STATE : SIGNIN_UNCONFIRMED_STATE});
    }
  };

  // A cancellation is on its way back to checkout and has nothing to say; every
  // other outcome renders a popup, because this route has no page behind it and
  // rendering nothing is what left the user on a blank screen.
  if (isCancelled) {
    return null;
  }

  return (
    <>
      {paymentStatus === "success" && showConfetti && (
        <Confetti
          width={width}
          height={height}
          numberOfPieces={450}
          recycle={false}
          gravity={0.5}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            // Above the success modal overlay (z-index 10000) so the confetti sits in
            // the foreground instead of behind its blurred backdrop.
            zIndex: 10001,
            pointerEvents: "none",
          }}
        />
      )}

      {isVerifying && (
        <ProcessingModal
          open={isVerifying}
          elapsedSeconds={elapsedSeconds}
        />
      )}
      {!isVerifying &&
        isModalOpen &&
        (paymentStatus === "success" ? (
          <SuccessModal
            handleNavigation={successDestination}
            setModalOpen={handleModal}
            title={STATUS_TITLES.succeeded}
            desc={msg}
            btnText="Close"
          />
        ) : (
          <ErrorModal
            handleNavigation={closeDestination}
            setModalOpen={handleModal}
            info={isInfoResult}
            title={resultTitle}
            desc={resultMessage}
            btnText={shouldReturnToCheckout ? "Try again" : "Close"}
          />
        ))}
    </>
  );
}

export default PostPayment;
